import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CryptoModal from "../CryptoModal";
import { ChevronRightIcon } from "../../icons";
import TransferHeaderTabs from "./TransferHeaderTabs";
import SelectAssetStep from "./SelectAssetStep";
import CreateNewWalletStep from "./CreateNewWalletStep";
import DepositAddressStep from "./DepositAddressStep";
import WithdrawEnterDetailsStep from "./WithdrawEnterDetailsStep";
import WithdrawConfirmOrderStep from "./WithdrawConfirmOrderStep";
import ProcessingStep from "./ProcessingStep";
import { useDispatch, useSelector } from "react-redux";
import { getCryptoDepositAddress } from "../../../redux/actions/CryptoTransaction/GetDepositAddress";
import useGuideHint from "../GetStartedGuide/useGuideHint";
import WithdrawComplete from "./WithdrawComplete";
import ConfirmDepositAsset from "./ConfirmDepositAsset";
import ModalStepper from "../common/ModalStepper";


// Step labels per tab — index matches currentStep (1-based, so labels[step - 1])
const DEPOSIT_LABELS = ["Select Asset", "Confirm Asset", "Deposit Address"];
const WITHDRAW_LABELS = ["Select Asset", "Enter Details", "Confirm Order", "Complete Order"];

const TRANSFER_DEPOSIT_STEPS = [
  { id: 1, label: "Select Asset" },
  { id: 2, label: "Confirm Asset" },
  { id: 3, label: "Deposit Address" },
];

const TRANSFER_WITHDRAW_STEPS = [
  { id: 1, label: "Select Asset" },
  { id: 2, label: "Enter Details" },
  { id: 3, label: "Confirm Order" },
];

const TransferModal = ({
  isOpen,
  onClose,
  showDepositGuide = false,
  isNewUser = false,
  totalBalanceUsd = 0,
  defaultTab = "Deposit",
  beneficiary = null,
  beneficiaryAsset = null,
  defaultStep = 1,
  onGuideBuyCrypto,
  onGuideComplete,
  setLoadData
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [currentStep, setCurrentStep] = useState(defaultStep);
  const [modalPhase, setModalPhase] = useState("form");
  const [depositStep2Phase, setDepositStep2Phase] = useState("network");
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(beneficiaryAsset);
  const [selectedNetwork, setSelectedNetwork] = useState(null);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState(beneficiary);
  const [amount, setAmount] = useState("");
  const [guideSkipped, setGuideSkipped] = useState(false);
  const cryptoAccounts = useSelector((state) => state.client.clientInfo.crypto_accounts);
  const dispatch = useDispatch();

  const [networkLoading, setNetworkLoading] = useState(false);
  const [walletProcessing, setWalletProcessing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cryptoAddress, setCryptoAddress] = useState(null);
  const [error, setError] = useState(null);
  const [isReturningFromBack, setIsReturningFromBack] = useState(false);

  const labels = activeTab === "Deposit" ? DEPOSIT_LABELS : WITHDRAW_LABELS;

  const resetFormState = () => {
    setCurrentStep(defaultStep);
    setModalPhase("form");
    setSelectedAccount(null);
    setSelectedAsset(beneficiaryAsset);
    setSelectedNetwork(null);
    setSelectedBeneficiary(beneficiary);
    setAmount("");
    setNetworkLoading(false);
    setWalletProcessing(false);
    setCryptoAddress(null);
    setSubmitting(null);
    setIsReturningFromBack(false);
  };

  const resetAllState = () => {
    setActiveTab(defaultTab);
    resetFormState();
    setGuideSkipped(false);
  };

  const handleClose = () => {
    onClose();
  };

  const handleAddBeneficiary = () => {
    handleClose();
    navigate("/v2/manage_crypto_beneficiaries");
  };

  const handleBack = () => {
    if (modalPhase !== "form") return;

    // 1. Inside Step 2 in "create-wallet" mode
    if (activeTab === "Deposit" && currentStep === 2 && depositStep2Phase === "create-wallet") {
      setDepositStep2Phase("network");
      setIsReturningFromBack(true); // Prevent re-triggering wallet creation automatically
      return;
    }

    // 2. Main modal step navigation
    if (currentStep > 1) {
      if (currentStep === 2) {
        setSelectedAsset(null);
        setSelectedNetwork(activeTab === "Withdraw" ? "" : null);
        setSelectedBeneficiary(null);
        setAmount("");
        setIsReturningFromBack(false);
      }

      if (currentStep === 3) {
        // Returning from Address to Step 2
        setDepositStep2Phase("network");
        setSelectedBeneficiary(null);
        setAmount("");
        setIsReturningFromBack(true); // Flag that user backed out from address view
      }

      setCurrentStep((step) => step - 1);
      return;
    }

    handleClose();
  };

  const handleAssetSelect = (asset) => {
    setSelectedAsset(asset);
    setDepositStep2Phase("network");
    setSelectedBeneficiary(null);
    setSelectedNetwork(activeTab === "Withdraw" ? "" : null);
    setAmount("");
    setIsReturningFromBack(false); // Fresh asset select: auto-proceed is allowed
    setCurrentStep(2);
  };

  const handleNetworkSwitch = () => {
    setDepositStep2Phase("network");
    setIsReturningFromBack(true); // Don't auto-advance if user explicitly wants to switch
    setCurrentStep(2);
  };

  // const handleNetworkSelect = async (network) => {
  //   setSelectedNetwork(network);
  // };

  const handleContinue = async (networkOverride = null) => {
    setWalletProcessing(true);

    // Fall back to state if no override was passed (e.g. manual user button click)
    const activeNetwork = selectedNetwork || networkOverride;

    try {
      const response = await dispatch(
        getCryptoDepositAddress({
          account_id: selectedAsset.id,
          network: activeNetwork?.network_id,
        })
      );

      const address = response?.data ?? null;

      if (address) {
        setCryptoAddress(address);
        setCurrentStep(3);
      } else {
        setDepositStep2Phase("create-wallet");
      }
    } catch (error) {
      console.error(error);
    } finally {
      setWalletProcessing(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      resetAllState();
    }
    // eslint-disable-next-line
  }, [isOpen]);

  useEffect(() => {
    if (activeTab !== "Deposit") {
      setDepositStep2Phase("network");
      setSelectedNetwork(null);
      setSelectedBeneficiary(null);
      setAmount("");
    } else {
      setSelectedNetwork(null);
      setSelectedBeneficiary(null);
      setAmount("");
    }
  }, [activeTab]);

  const showCreateNewWalletStep =
    modalPhase === "form" &&
    activeTab === "Deposit" &&
    currentStep === 2 &&
    depositStep2Phase === "create-wallet";

  const showWithdrawProcessing = modalPhase === "processing" && activeTab === "Withdraw";
  const useFullHeightContent = showCreateNewWalletStep || showWithdrawProcessing;
  const showStepper =
    !showCreateNewWalletStep &&
    !showWithdrawProcessing &&
    (modalPhase === "form" || modalPhase === "processing" || modalPhase === "success");
  const stepperStep = modalPhase === "form" ? currentStep : 4;

  const {
    selectAssetGuideHint,
    selectNetworkGuideHint,
    copyAddressGuideHint,
    inlineMobileGuideHint,
    mobileGuideDimActive,
    guideHint,
  } = useGuideHint({
    showDepositGuide: isOpen && showDepositGuide,
    guideSkipped,
    setGuideSkipped,
    activeTab,
    modalPhase,
    currentStep,
    depositStep2Phase,
    onGuideBuyCrypto,
    onGuideComplete,
  });

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab !== activeTab) resetFormState();
  };

  const renderStepContent = (step) => {
    if (modalPhase === "processing") {
      return <ProcessingStep centered={showWithdrawProcessing} />;
    }

    const currentLabel = labels[step - 1];

    switch (currentLabel) {
      case "Select Asset":
        return (
          <SelectAssetStep
            onAssetSelect={handleAssetSelect}
            spotlightActive={Boolean(selectAssetGuideHint)}
            guideHint={selectAssetGuideHint}
            cryptoAccounts={cryptoAccounts}
          />
        );

      case "Confirm Asset":
        if (depositStep2Phase === "create-wallet") {
          return (
            <div className="flex h-full min-h-0 w-full flex-1 flex-col">
              <CreateNewWalletStep
                selectedAsset={selectedAsset}
                selectedNetwork={selectedNetwork}
                onComplete={handleContinue}
                onCancel={() => {
                  setCurrentStep(1);
                }}
                // onCancel={handleBack}
              />
            </div>
          );
        }

        if (networkLoading || walletProcessing) {
          return <ProcessingStep centered />;
        }

        return (
          <ConfirmDepositAsset
            selectedNetwork={selectedNetwork}
            onNetworkSelect={setSelectedNetwork}
            handleContinue={handleContinue}
            spotlightActive={Boolean(selectNetworkGuideHint)}
            guideHint={selectNetworkGuideHint}
            selectedAssetId={selectedAsset?.id}
            skipAutoContinue={isReturningFromBack}
          />
        );

      case "Deposit Address":
        return (
          <DepositAddressStep
            selectedAsset={selectedAsset}
            selectedNetwork={selectedNetwork}
            onNetworkSwitch={handleNetworkSwitch}
            onSaveShare={handleClose}
            spotlightActive={Boolean(copyAddressGuideHint)}
            guideHint={copyAddressGuideHint}
            depositAddress={cryptoAddress}
          />
        );

      case "Enter Details":
        return (
          <WithdrawEnterDetailsStep
            selectedAsset={selectedAsset}
            selectedBeneficiary={selectedBeneficiary}
            selectedNetwork={selectedNetwork}
            amount={amount}
            onBeneficiaryChange={setSelectedBeneficiary}
            onNetworkChange={setSelectedNetwork}
            onAmountChange={setAmount}
            onContinue={() => setCurrentStep(3)}
            onAddBeneficiary={handleAddBeneficiary}
            error={error}
            setError={setError}
          />
        );

      case "Confirm Order":
        if (submitting) {
          return <ProcessingStep centered />;
        }

        return (
          <WithdrawConfirmOrderStep
            selectedAsset={selectedAsset}
            selectedNetwork={selectedNetwork}
            selectedBeneficiary={selectedBeneficiary}
            amount={amount}
            submitting={submitting}
            setSubmitting={setSubmitting}
            onConfirm={() => setCurrentStep(4)}
            setCurrentStep={setCurrentStep}
            setError={setError}
          />
        );

      case "Complete Order":
        return (
          <WithdrawComplete
            activeTab={activeTab}
            selectedAsset={selectedAsset}
            selectedCryptoAccount={selectedAccount}
            amount={amount}
            onCheckAssets={() => {
              setLoadData((prev) => !prev);
              handleClose()
            } 
          }
          />
        );

      default:
        return null;
    }
  };

  return (
    <CryptoModal
      isOpen={isOpen}
      onClose={handleClose}
      mobileFullScreen
      mobileSpotlightActive={mobileGuideDimActive}
      wrapperClassName={"w-full max-w-none sm:max-w-[600px]"}
      guideHint={guideHint}
      closeOnOutsideClick={false}
      panelClassName={`flex flex-col sm:px-4 ${
        mobileGuideDimActive ? "max-sm:overflow-visible" : "overflow-hidden"
      }`}
      headerLeft={
        <button
          type="button"
          onClick={handleBack}
          className="rounded-full p-0 custom-transition hover:bg-option-hover"
          aria-label="Go back"
        >
          <ChevronRightIcon className="rotate-180 text-text-primary" />
        </button>
      }
      headerTabs={
        <TransferHeaderTabs
          active={activeTab}
          onChange={handleTabChange}
          isNewUser={isNewUser}
          totalBalanceUsd={totalBalanceUsd}
        />
      }
    >
      <div
        className={`flex min-h-0 flex-1 flex-col ${
          mobileGuideDimActive
            ? "max-sm:overflow-visible"
            : "overflow-hidden"
        }`}
      >
        {showStepper && (
          <>
            <ModalStepper
              steps={
                activeTab === "Deposit"
                  ? TRANSFER_DEPOSIT_STEPS
                  : TRANSFER_WITHDRAW_STEPS
              }
              currentStep={stepperStep}
            />
            <div className="mx-4 max-sm:mb-2 max-sm:mt-8 mt-3 shrink-0 border-t border-divider sm:mx-6 sm:mt-4" />
          </>
        )}

        <div
          className={`flex min-h-0 w-full flex-1 flex-col font-outfit 
            ${useFullHeightContent
              ? "h-full flex-1"
              : inlineMobileGuideHint
              ? "shrink-0 sm:min-h-0 sm:flex-1"
              : "min-h-0 flex-1"
            } 
            ${mobileGuideDimActive ? "max-sm:overflow-visible" : "overflow-hidden"}
          `}
        >
          {renderStepContent(currentStep)}
        </div>
      </div>
    </CryptoModal>
  );
};

export default TransferModal;